import { useId, useMemo, useState } from 'react'

interface TagOption {
  readonly name: string
  readonly isNew: boolean
}

export function TagInput({
  value,
  selected,
  known,
  onChange,
  onAdd,
  onRemove,
}: {
  readonly value: string
  readonly selected: readonly string[]
  readonly known: readonly string[]
  readonly onChange: (value: string) => void
  readonly onAdd: (value: string) => void
  readonly onRemove: (value: string) => void
}) {
  const listId = useId()
  const hintId = useId()
  // The card area is no longer the only place this stands — the session makes cards too.
  const fieldId = useId()
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const options = useMemo(() => suggestions(value, selected, known), [known, selected, value])
  const expanded = open && options.length > 0
  const activeOption = options[Math.min(active, Math.max(0, options.length - 1))]

  const add = (name: string) => {
    onAdd(name)
    onChange('')
    setActive(0)
    setOpen(true)
  }

  return (
    <div className="tag-field">
      <label className="card-field__label" htmlFor={fieldId}>
        Tags
      </label>
      <div className="tag-field__combobox">
        <div className="tag-field__input">
          {selected.map((tag) => (
            <button
              type="button"
              tabIndex={-1}
              className="tag tag--removable"
              aria-label={`Remove tag: ${tag}`}
              key={tag}
              onClick={() => {
                onRemove(tag)
              }}
            >
              <span>{tag}</span>
              <span aria-hidden="true">×</span>
            </button>
          ))}
          <input
            id={fieldId}
            role="combobox"
            value={value}
            placeholder={selected.length === 0 ? 'Find or create a tag' : 'Another tag'}
            aria-autocomplete="list"
            aria-expanded={expanded}
            aria-controls={listId}
            aria-activedescendant={
              expanded && activeOption !== undefined ? `${listId}-${active}` : undefined
            }
            aria-describedby={hintId}
            onFocus={() => {
              setOpen(true)
            }}
            onBlur={() => {
              setOpen(false)
            }}
            onChange={(event) => {
              onChange(event.target.value)
              setActive(0)
              setOpen(true)
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' && options.length > 0) {
                event.preventDefault()
                setOpen(true)
                setActive((before) => (before + 1) % options.length)
              } else if (event.key === 'ArrowUp' && options.length > 0) {
                event.preventDefault()
                setOpen(true)
                setActive((before) => (before - 1 + options.length) % options.length)
              } else if (
                (event.key === 'Enter' || event.key === 'Tab') &&
                !event.metaKey &&
                !event.ctrlKey &&
                value.trim() !== ''
              ) {
                event.preventDefault()
                add(expanded && activeOption !== undefined ? activeOption.name : value)
              } else if (event.key === 'Escape') {
                event.preventDefault()
                setOpen(false)
              } else if (event.key === 'Backspace' && value === '' && selected.length > 0) {
                onRemove(selected[selected.length - 1] ?? '')
              }
            }}
          />
        </div>
        {expanded && (
          <ul
            className="tag-field__options"
            id={listId}
            role="listbox"
            aria-label="Tag suggestions"
          >
            {options.map((option, index) => (
              <li
                id={`${listId}-${index}`}
                role="option"
                aria-label={`${option.name}, ${option.isNew ? 'Create tag' : 'Existing tag'}`}
                aria-selected={index === active}
                className="tag-field__option"
                key={`${option.isNew ? 'new' : 'known'}-${option.name}`}
                onMouseDown={(event) => {
                  event.preventDefault()
                }}
                onClick={() => {
                  add(option.name)
                }}
              >
                <span>{option.name}</span>
                <small>{option.isNew ? 'Create tag' : 'Existing tag'}</small>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="tag-field__hint" id={hintId}>
        Type to filter. Enter or Tab selects a suggestion or creates the name as one tag.
      </p>
    </div>
  )
}

function suggestions(
  value: string,
  selected: readonly string[],
  known: readonly string[],
): TagOption[] {
  const query = value.trim().toLocaleLowerCase()
  const selectedNames = selected.map((tag) => tag.toLocaleLowerCase())
  const available = known.filter((tag) => !selectedNames.includes(tag.toLocaleLowerCase()))
  const matching = available
    .filter((tag) => query === '' || tag.toLocaleLowerCase().includes(query))
    .map((name) => ({ name, isNew: false }))
  const knownExactly = known.some((tag) => tag.toLocaleLowerCase() === query)
  return query !== '' && !knownExactly
    ? [...matching, { name: value.trim(), isNew: true }]
    : matching
}
